import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { InitialAddEditWoodChipFormData } from '../Data/WoodChipFormDataFields';
import { useToast } from 'context/tostifyContext';

const useWoodChipSampleController = () => {
  const [woodchipSampleData, setWoodchipSampleData] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [selectedWoodchipSampleData, setSelectedWoodchipSampleData] = useState(InitialAddEditWoodChipFormData);
  const [openWoodchipAddEditModal, setOpenWoodchipAddEditModal] = useState(false);
  const [isRetestMode, setIsRetestMode] = useState(false);
  const [activeTab, setActiveTab] = useState('Pending');

  const dispatch = useDispatch();
  const isMounted = useRef(true);
  const { showToast } = useToast();

  useEffect(() => {
    isMounted.current = true;
    getWoodchipSampleData();
    getDockets();
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ─── Derived filtered lists ────────────────────────────────────────────────

  const pendingSamples = woodchipSampleData.filter(s => s.sampleStatus === 'Pending');
  const completedSamples = woodchipSampleData.filter(s => s.sampleStatus === 'Completed');
  const retestSamples = woodchipSampleData.filter(s => s.sampleStatus === 'Retest');

  // ─── Data fetching ─────────────────────────────────────────────────────────

  const getWoodchipSampleData = () => {
    dispatch({ type: FETCH_START });
    customAxios
      .get('/Ops/GetWoodchipSamples', { headers: authHeader() })
      .then(response => {
        if (isMounted.current) {
          const formatted = response.data.map(s => ({
            ...s,
            mC_Percent: s.mC_Percent != null ? Number(s.mC_Percent).toFixed(2) : null,
          }));
          setWoodchipSampleData(formatted);
          dispatch({ type: FETCH_SUCCESS });
        }
      })
      .catch(error => {
        if (isMounted.current) {
          console.error('Error fetching woodchip samples:', error);
          dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        }
      });
  };

  const getDockets = () => {
    customAxios
      .get('/Ops/GetFilteredDockets?filterColumn=productType&filterValue=Woodchip', { headers: authHeader() })
      .then(response => {
        if (isMounted.current) {
          setDockets(response.data || []);
        }
      })
      .catch(error => {
        console.error('Error fetching dockets:', error);
      });
  };

  // ─── Modal open ────────────────────────────────────────────────────────────

  const handleWoodchipAddEditModalOpen = (sampleData, retestMode = false) => {
    setIsRetestMode(retestMode);
    setSelectedWoodchipSampleData(sampleData ?? InitialAddEditWoodChipFormData);
    setOpenWoodchipAddEditModal(true);
  };

  // ─── Submit: Pending → Completed ──────────────────────────────────────────
  //
  // All saves from this modal hit EditWoodchipSamples because the record already
  // exists in Pending state. We just fill in the lab results and flip the status.
  //
  // testNo logic:
  //   - First completion (Pending → Completed): testNo = 1
  //   - Retest completion: increment existing testNo  [reserved for later]

  const addHandleWoodChipSampleDataSubmit = async (isEdit, sampleData) => {
    const url = '/Ops/EditWoodchipSamples';

    const payload = {
      id: sampleData.id,
      whDocketNumber: Number(sampleData.wH_DocketNumber),
      finishWeight: sampleData.finishWeight !== '' ? Number(sampleData.finishWeight) : null,
      customer: sampleData.customer,
      spNumber: sampleData.sP_Number,
      mcPercent: sampleData.mC_Percent !== '' ? Number(Number(sampleData.mC_Percent).toFixed(2)) : null,
      finishDate: sampleData.finishDate || null,
      startingWeight: sampleData.startingWeight !== '' ? Number(sampleData.startingWeight) : null,
      cookTimeHours: sampleData.cookTimeHours !== '' ? Number(sampleData.cookTimeHours) : null,
      testNo: sampleData.testNo ?? 1,
      sampleStatus: 'Completed',
    };

    dispatch({ type: FETCH_START });
    try {
      // Step 1 — save lab results
      const saveRes = await customAxios.post(url, payload, { headers: authHeader() });
      if (saveRes.status !== 200) {
        showToast(
          isEdit ? 'Woodchip sample completed successfully.' : `Retest (Test #${payload.testNo}) submitted successfully.`,
          'success',
        );
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        return; // ← bail, don't touch status
      }

      // Step 2 — flip status only after data is confirmed saved
      await changeSampleStatus(sampleData.wH_DocketNumber, 'Completed');

      setOpenWoodchipAddEditModal(false);
      setIsRetestMode(false);
      getWoodchipSampleData();
      dispatch({ type: FETCH_SUCCESS });
      showToast('Woodchip Sample completed successfully', 'success');
    } catch (error) {
      console.error('Error submitting woodchip sample:', error);
      showToast('Error: Something went wrong.', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const changeSampleStatus = async (docketNumber, newStatus) => {
    if (!docketNumber) {
      showToast('Error: Docket number is required to change sample status.', 'error');
      return;
    }

    try {
      const response = await customAxios.post(
        '/Ops/ChangeSampleStatus',
        { docketNumber, sampleStatus: newStatus },
        { headers: authHeader() },
      );
      if (response.status === 200) {
        getWoodchipSampleData();
        showToast(`Sample status changed to ${newStatus}.`, 'success');
      }
    } catch (error) {
      console.error('Error changing sample status:', error);
      showToast('Error: Failed to change sample status.', 'error');
    }
  };

  // ─── Mark for Retest: Completed → Retest ──────────────────────────────────

  const handleMarkForRetest = async sampleData => {
    const payload = {
      whDocketNumber: Number(sampleData.wH_DocketNumber),
      finishWeight: null,
      customer: sampleData.customer,
      spNumber: sampleData.sP_Number,
      mcPercent: null,
      finishDate: null,
      startingWeight: null,
      cookTimeHours: null,
      testNo: sampleData.testNo + 1,
    };

    dispatch({ type: FETCH_START });
    try {
      const response = await customAxios.post('/Ops/AddWoodchipSamples', payload, { headers: authHeader() });
      if (response.status === 200) {
        await changeSampleStatus(sampleData.wH_DocketNumber, 'Retest');

        getWoodchipSampleData();
        dispatch({ type: FETCH_SUCCESS });
        showToast('Sample added for retest.', 'success');
      } else {
        showToast('Error: Failed to add sample for retest.', 'error');
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }
    } catch (error) {
      console.error('Error adding sample for retest:', error);
      showToast('Error: Something went wrong.', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  return {
    woodchipSampleData,
    pendingSamples,
    completedSamples,
    retestSamples,
    dockets,
    activeTab,
    setActiveTab,
    selectedWoodchipSampleData,
    openWoodchipAddEditModal,
    setOpenWoodchipAddEditModal,
    isRetestMode,
    handleWoodchipAddEditModalOpen,
    addHandleWoodChipSampleDataSubmit,
    handleMarkForRetest,
    changeSampleStatus,
  };
};

export default useWoodChipSampleController;
